import type { ReactNode } from "react"
import { useInView } from "@/hooks/use-in-view"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Check, X, Minus } from "lucide-react"

const YES = <Check className="h-4 w-4 text-green-600 dark:text-green-400 mx-auto" />
const NO = <X className="h-4 w-4 text-red-400/60  dark:text-red-400/50  mx-auto" />
const UNKN = <Minus className="h-4 w-4 text-sky-400/40  dark:text-sky-400/30  mx-auto" />

interface Row {
	feature: string
	beediv: ReactNode
	divtracker: ReactNode
	stockevents: ReactNode
}

const ROWS: Row[] = [
	{ feature: "Portfolios", beediv: "Unlimited", divtracker: "1", stockevents: "Limited" },
	{ feature: "Holdings", beediv: "Unlimited", divtracker: "5", stockevents: "Limited" },
	{ feature: "Dividend calendar", beediv: YES, divtracker: YES, stockevents: YES },
	{ feature: "Dividend forecast", beediv: YES, divtracker: "Limited", stockevents: YES },
	{ feature: "Investment simulator", beediv: YES, divtracker: NO, stockevents: NO },
	{ feature: "FIRE dashboard", beediv: YES, divtracker: NO, stockevents: NO },
	{ feature: "CSV import/export", beediv: YES, divtracker: YES, stockevents: UNKN },
	{ feature: "ICS calendar feed", beediv: YES, divtracker: NO, stockevents: NO },
	{ feature: "Demo mode", beediv: YES, divtracker: NO, stockevents: NO },
	{ feature: "Multi-currency", beediv: "All major", divtracker: UNKN, stockevents: UNKN },
	{ feature: "International stocks", beediv: "60+ exchanges", divtracker: UNKN, stockevents: UNKN },
	{
		feature: "Price",
		beediv: <span className="font-bold text-green-600 dark:text-green-400">Free</span>,
		divtracker: "Free / $29.99/yr",
		stockevents: "Free / $$",
	},
]

function Cell({ value }: { value: ReactNode }) {
	return typeof value === "string" ? <span className="text-xs sm:text-sm">{value}</span> : <>{value}</>
}

export function CompetitorTable() {
	const { ref, inView } = useInView()

	return (
		<div ref={ref} className={`transition-opacity duration-700 ease-out ${inView ? "opacity-100" : "opacity-0"}`}>
			<section className="py-20 px-4 bg-sky-50/40 dark:bg-sky-950/20">
				<div className="max-w-4xl mx-auto">
					<div className="text-center mb-10">
						<h2 className="text-3xl sm:text-4xl font-bold text-sky-950 dark:text-sky-50">
							More features. Lower price.
						</h2>
						<p className="mt-3 text-base sm:text-lg text-sky-900/65 dark:text-sky-300/55">
							BeeDiv vs the leading dividend trackers.
						</p>
					</div>

					<div className="rounded-2xl overflow-x-auto border border-sky-100/60 dark:border-sky-800/30 bg-white/60 dark:bg-sky-950/60 backdrop-blur-sm">
						<Table>
							<TableHeader>
								<TableRow className="border-sky-100/60 dark:border-sky-800/30 hover:bg-transparent">
									<TableHead className="text-sky-700/70 dark:text-sky-400/60 font-semibold w-1/3 py-3 sm:py-4 pl-2 sm:pl-4 text-xs sm:text-sm">
										Feature
									</TableHead>
									<TableHead className="text-center font-bold text-sky-700 dark:text-sky-200 bg-sky-50/80 dark:bg-sky-900/40 py-3 sm:py-4 text-xs sm:text-sm">
										<div className="flex flex-col items-center gap-0.5">
											<span>BeeDiv</span>
											<span className="text-xs font-normal text-green-600 dark:text-green-400">
												Free
											</span>
										</div>
									</TableHead>
									<TableHead className="text-center text-sky-600/60 dark:text-sky-400/50 font-semibold py-3 sm:py-4 text-xs sm:text-sm">
										DivTracker
									</TableHead>
									<TableHead className="text-center text-sky-600/60 dark:text-sky-400/50 font-semibold py-3 sm:py-4 pr-2 sm:pr-4 text-xs sm:text-sm">
										Stock Events
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{ROWS.map(({ feature, beediv, divtracker, stockevents }) => (
									<TableRow key={feature} className="border-sky-100/40 dark:border-sky-800/20">
										<TableCell className="font-medium text-sky-800 dark:text-sky-200 text-xs sm:text-sm py-2 sm:py-3 pl-2 sm:pl-4">
											{feature}
										</TableCell>
										<TableCell className="text-center bg-sky-50/50 dark:bg-sky-900/20 py-2 sm:py-3 px-2 sm:px-4">
											<Cell value={beediv} />
										</TableCell>
										<TableCell className="text-center text-sky-700/60 dark:text-sky-400/50 py-2 sm:py-3 px-2 sm:px-4">
											<Cell value={divtracker} />
										</TableCell>
										<TableCell className="text-center text-sky-700/60 dark:text-sky-400/50 py-2 sm:py-3 pr-2 sm:pr-4 px-2 sm:px-4">
											<Cell value={stockevents} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>

					<p className="mt-4 text-center text-xs text-sky-500/60 dark:text-sky-400/40">
						Competitor data based on publicly available free-tier feature pages.
					</p>
				</div>
			</section>
		</div>
	)
}
